/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Descriptions,
  Form,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import MainLayout from '../../components/common/MainLayout';
import {
  useCreateSeasonMutation,
  useGetDebugTemplatesQuery,
  useGetSeasonAttemptsQuery,
  useGetSeasonLeaderboardQuery,
  useGetSeasonQuery,
  useListSeasonsQuery,
  useVerifySeasonMutation,
  useCloseSeasonMutation,
  type LeaderboardEntry,
  type SeasonAttempt,
  type SeasonSummary,
} from '../../api/seasonsAdminApi';

const { Text } = Typography;

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('ru-RU');
};

const modeTag = (mode: string) => (mode === 'blind'
  ? <Tag color="gold">Слепой</Tag>
  : <Tag color="blue">Открытый</Tag>);

/** manifest типизирован как Record<string, unknown> — читаем скалярами, иначе '—'. */
const manifestScalar = (manifest: Record<string, unknown>, key: string): string | null => {
  const value = manifest[key];
  if (typeof value === 'string') return value || null;
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
};

const manifestObject = (manifest: Record<string, unknown>, key: string): Record<string, unknown> => {
  const value = manifest[key];
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
};

const hashCell = (value: string | null) => {
  if (!value) return <Text type="secondary">—</Text>;
  const shown = value.length > 20 ? `${value.slice(0, 16)}…` : value;
  return <Text code copyable={{ text: value }}>{shown}</Text>;
};

const attemptStatusTag = (attempt: SeasonAttempt) => {
  if (attempt.status === 'verified') return <Tag color="green">верифицирована</Tag>;
  if (attempt.status === 'rejected') return <Tag color="red">дисквалификация</Tag>;
  if (attempt.scoreSource === 'client') return <Tag color="gold">предварительная</Tag>;
  return <Tag>отправлена</Tag>;
};

const scoreCell = (score: SeasonAttempt['serverScore'] | SeasonAttempt['provisionalScore']) => {
  if (!score) return <Text type="secondary">—</Text>;
  return (
    <Space size={4}>
      <Tag color={score.scoreTier === 3 ? 'blue' : score.scoreTier === 2 ? 'red' : 'default'}>
        tier {score.scoreTier ?? '—'}
      </Tag>
      <Text>{score.passedCount ?? '—'}/{score.total ?? '—'}</Text>
    </Space>
  );
};

/** Развёрнутая панель одного сезона: лидерборд + попытки + верификация. */
const SeasonDetail: React.FC<{ season: SeasonSummary }> = ({ season }) => {
  const { data: board, isFetching: boardLoading } = useGetSeasonLeaderboardQuery(season.id);
  const { data: attemptsData, isFetching: attemptsLoading } = useGetSeasonAttemptsQuery(season.id);
  // Снапшот + манифест отдаёт только GET /seasons/:id — в списке их нет.
  const { data: frozen, isFetching: frozenLoading } = useGetSeasonQuery(season.id);
  const [verifySeason, { isLoading: verifying }] = useVerifySeasonMutation();
  const [closeSeason, { isLoading: closing }] = useCloseSeasonMutation();

  const manifest = (frozen?.season.manifest || season.manifest || {}) as Record<string, unknown>;
  const manifestWindow = manifestObject(manifest, 'window');
  const signature = frozen?.season.signature || season.signature || '';
  const snapshot = frozen?.season.template;

  const pendingCount = useMemo(
    () => (attemptsData?.attempts || []).filter(
      (a) => a.status === 'submitted' && a.scoreSource === 'client',
    ).length,
    [attemptsData],
  );

  const onVerify = async () => {
    try {
      const result = await verifySeason({ seasonId: season.id }).unwrap();
      message.success(
        `Проверено ${result.checked}: верифицировано ${result.verified}, дисквалифицировано ${result.rejected}`,
      );
    } catch (err: unknown) {
      const detail = (err as { data?: { message?: string } })?.data?.message;
      message.error(detail || 'Верификация не удалась');
    }
  };

  const onClose = async (force: boolean) => {
    try {
      const result = await closeSeason({ seasonId: season.id, force }).unwrap();
      const issued = result.placementsIssued?.length ?? 0;
      message.success(result.alreadyClosed
        ? 'Сезон уже был закрыт: призы не дублируются'
        : `Сезон закрыт: верифицировано ${result.verified ?? 0}, брошено ${result.abandoned ?? 0}, призов за места ${issued}`);
    } catch (err: unknown) {
      const data = (err as { data?: { message?: string; code?: string } })?.data;
      message.error(data?.code === 'SEASON_STILL_OPEN'
        ? 'Окно сезона ещё открыто — закрыть можно принудительно'
        : data?.message || 'Закрыть сезон не удалось');
    }
  };

  const closedAlready = season.status === 'closed';
  const windowPassed = new Date(season.closesAt).getTime() <= Date.now();

  const leaderboardColumns: ColumnsType<LeaderboardEntry> = [
    { title: '#', dataIndex: 'rank', width: 48 },
    {
      title: 'Игрок',
      dataIndex: 'displayName',
      render: (name: string | null, row) => name || <Text type="secondary">{row.userId.slice(0, 8)}…</Text>,
    },
    {
      title: 'Счёт',
      key: 'score',
      render: (_, row) => (
        <Space size={4}>
          <Tag color={row.scoreTier === 3 ? 'blue' : row.scoreTier === 2 ? 'red' : 'default'}>
            tier {row.scoreTier ?? '—'}
          </Tag>
          <Text>{row.passedCount ?? '—'}/{row.total ?? '—'}</Text>
          {row.primaryMetricKey ? (
            <Text type="secondary">
              {row.primaryMetricKey}: {row.primaryMetricValue ?? '—'}
            </Text>
          ) : null}
        </Space>
      ),
    },
    {
      title: 'Статус',
      key: 'status',
      width: 150,
      render: (_, row) => (row.verified
        ? <Tag color="green">верифицирован</Tag>
        : row.provisional
          ? <Tooltip title="Счёт из клиентского предпросмотра — требует верификации"><Tag color="gold">предварительный</Tag></Tooltip>
          : <Tag>—</Tag>),
    },
    { title: 'Отправлено', dataIndex: 'submittedAt', render: formatDateTime, width: 170 },
  ];

  const attemptColumns: ColumnsType<SeasonAttempt> = [
    {
      title: 'Игрок',
      dataIndex: 'displayName',
      render: (name: string | null, row) => name || <Text type="secondary">{row.userId.slice(0, 8)}…</Text>,
    },
    { title: 'Статус', key: 'status', width: 160, render: (_, row) => attemptStatusTag(row) },
    {
      title: 'Server score',
      key: 'server',
      render: (_, row) => scoreCell(row.serverScore),
    },
    {
      title: 'Provisional',
      key: 'prov',
      render: (_, row) => scoreCell(row.provisionalScore),
    },
    { title: 'Событий', dataIndex: 'eventCount', width: 90 },
    { title: 'Отправлена', dataIndex: 'submittedAt', render: formatDateTime, width: 170 },
    { title: 'Верифицирована', dataIndex: 'verifiedAt', render: formatDateTime, width: 170 },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Descriptions size="small" column={4}>
        <Descriptions.Item label="Шаблон">{season.scenarioTemplateId} v{season.version}</Descriptions.Item>
        <Descriptions.Item label="Окно">
          {formatDateTime(season.opensAt)} → {formatDateTime(season.closesAt)}
        </Descriptions.Item>
        <Descriptions.Item label="Попыток/юзера">{season.attemptsLimit}</Descriptions.Item>
        <Descriptions.Item label="Движок">{season.engineVersion} / {season.scoringVersion}</Descriptions.Item>
      </Descriptions>

      <Space wrap>
        {season.verificationPolicy === 'lazy' && (
          <>
            <Button type="primary" onClick={onVerify} loading={verifying} disabled={pendingCount === 0 || closedAlready}>
              Верифицировать ({pendingCount} в очереди)
            </Button>
            {pendingCount === 0 && <Text type="secondary">очередь пуста</Text>}
          </>
        )}
        {closedAlready ? (
          <Tag color="default">закрыт {formatDateTime(season.closedAt)}</Tag>
        ) : (
          <Popconfirm
            title={windowPassed ? 'Закрыть сезон и раздать призы за места?' : 'Окно ещё открыто. Закрыть принудительно?'}
            description="Оставшиеся submitted будут верифицированы, черновики брошены; места получают только verified."
            okText="Закрыть"
            cancelText="Отмена"
            onConfirm={() => onClose(!windowPassed)}
          >
            <Button danger={!windowPassed} loading={closing}>
              {windowPassed ? 'Закрыть сезон' : 'Закрыть принудительно'}
            </Button>
          </Popconfirm>
        )}
      </Space>

      <Card size="small" title="Что заморожено" loading={frozenLoading}>
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="После правки техкарты или движка только templateHash показывает, на том ли шаблоне идёт сезон: снапшот заморожен при создании и живёт своей жизнью."
        />
        <Descriptions size="small" column={2} bordered>
          <Descriptions.Item label="templateHash" span={2}>
            {hashCell(manifestScalar(manifest, 'templateHash'))}
          </Descriptions.Item>
          <Descriptions.Item label="scoringVersion">
            {manifestScalar(manifest, 'scoringVersion') || <Text type="secondary">—</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="engineVersion">
            {manifestScalar(manifest, 'engineVersion') || <Text type="secondary">—</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Окно (манифест)" span={2}>
            {formatDateTime(manifestScalar(manifestWindow, 'opensAt'))}
            {' → '}
            {formatDateTime(manifestScalar(manifestWindow, 'closesAt'))}
          </Descriptions.Item>
          <Descriptions.Item label="Попыток (манифест)">
            {manifestScalar(manifest, 'attemptsLimit') || <Text type="secondary">—</Text>}
          </Descriptions.Item>
          <Descriptions.Item label="Подпись (HMAC)">
            {hashCell(signature || null)}
          </Descriptions.Item>
        </Descriptions>
        <Collapse
          size="small"
          style={{ marginTop: 12 }}
          items={[{
            key: 'snapshot',
            label: 'Снапшот шаблона (JSON)',
            children: (
              <>
                {season.mode === 'blind' && (
                  <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
                    Слепой сезон: weatherSeries вырезан из клиентской проекции, хэш при этом покрывает полный шаблон.
                  </Text>
                )}
                <pre
                  style={{
                    margin: 0,
                    maxHeight: 360,
                    overflow: 'auto',
                    fontFamily: 'monospace',
                    fontSize: 12,
                  }}
                >
                  {snapshot ? JSON.stringify(snapshot, null, 2) : '—'}
                </pre>
              </>
            ),
          }]}
        />
      </Card>

      <Card size="small" title="Лидерборд" loading={boardLoading}>
        <Table<LeaderboardEntry>
          rowKey={(row) => row.userId}
          columns={leaderboardColumns}
          dataSource={board?.entries || []}
          size="small"
          pagination={false}
          locale={{ emptyText: 'Пока нет попыток' }}
        />
      </Card>

      <Card size="small" title="Все попытки (аудит)" loading={attemptsLoading}>
        <Table<SeasonAttempt>
          rowKey={(row) => row.id}
          columns={attemptColumns}
          dataSource={attemptsData?.attempts || []}
          size="small"
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{ emptyText: 'Пока нет попыток' }}
        />
      </Card>
    </Space>
  );
};

const AdminSeasonsPage: React.FC = () => {
  const { data, isFetching, refetch } = useListSeasonsQuery();
  const { data: templatesData } = useGetDebugTemplatesQuery();
  const [createSeason, { isLoading: creating }] = useCreateSeasonMutation();
  const [createOpen, setCreateOpen] = useState(false);
  const [form] = Form.useForm();
  const selectedMode = Form.useWatch('mode', form) as string | undefined;

  const seasons = data?.seasons || [];
  const templates = templatesData?.templates || [];

  // Бэкенд отвечает 400 на blind + lazy: лог слепого сезона копится на сервере.
  useEffect(() => {
    if (selectedMode === 'blind') {
      form.setFieldsValue({ verificationPolicy: 'inline' });
    }
  }, [selectedMode, form]);

  const onCreate = async () => {
    try {
      const values = await form.validateFields();
      const created = await createSeason({
        templateId: values.templateId,
        mode: values.mode,
        opensAt: values.window[0].toISOString(),
        closesAt: values.window[1].toISOString(),
        attemptsLimit: values.attemptsLimit,
        verificationPolicy: values.mode === 'blind' ? 'inline' : values.verificationPolicy,
      }).unwrap();
      message.success(`Сезон запущен: ${created.id}`);
      setCreateOpen(false);
      form.resetFields();
    } catch (err: unknown) {
      const detail = (err as { data?: { message?: string } })?.data?.message;
      if (detail) message.error(detail);
    }
  };

  const columns: ColumnsType<SeasonSummary> = [
    {
      title: 'Шаблон',
      dataIndex: 'scenarioTemplateId',
      render: (id: string, row) => {
        const template = templates.find(
          (t) => t.scenarioTemplateId === id || t.slug === id,
        );
        return (
          <Space direction="vertical" size={0}>
            <Text strong>{template?.title || id}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{row.id}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      width: 100,
      render: (status: string) => <Tag color={status === 'open' ? 'green' : 'default'}>{status}</Tag>,
    },
    {
      title: 'Режим',
      dataIndex: 'mode',
      width: 110,
      render: (mode: string) => modeTag(mode),
    },
    {
      title: 'Верификация',
      dataIndex: 'verificationPolicy',
      width: 130,
      render: (policy: string) => (policy === 'lazy'
        ? <Tag color="gold">ленивая</Tag>
        : <Tag color="green">на сабмите</Tag>),
    },
    { title: 'Открыт', dataIndex: 'opensAt', render: formatDateTime, width: 170 },
    { title: 'Закрывается', dataIndex: 'closesAt', render: formatDateTime, width: 170 },
    { title: 'Попыток', dataIndex: 'attemptsLimit', width: 90 },
  ];

  return (
    <MainLayout>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card
            title="Турнирные сезоны"
            extra={(
              <Space>
                <Button onClick={() => refetch()}>Обновить</Button>
                <Button type="primary" onClick={() => setCreateOpen(true)}>Запустить сезон</Button>
              </Space>
            )}
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Сервер верит только логу действий: официальный счёт — всегда серверный реплей против подписанного снапшота. «Ленивые» сезоны принимают сабмит дёшево и реплеят по кнопке верификации."
            />
            <Table<SeasonSummary>
              rowKey={(row) => row.id}
              columns={columns}
              dataSource={seasons}
              loading={isFetching}
              expandable={{
                expandedRowRender: (season) => <SeasonDetail season={season} />,
              }}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              locale={{ emptyText: 'Открытых сезонов нет' }}
            />
          </Card>
        </Col>
      </Row>

      <Modal
        title="Запустить сезон"
        open={createOpen}
        onOk={onCreate}
        onCancel={() => setCreateOpen(false)}
        confirmLoading={creating}
        okText="Запустить"
        cancelText="Отмена"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            mode: 'open',
            attemptsLimit: 3,
            verificationPolicy: 'inline',
            window: [dayjs(), dayjs().add(14, 'day')],
          }}
        >
          <Form.Item
            name="templateId"
            label="Сценарий (серверный шаблон)"
            rules={[{ required: true, message: 'Выберите шаблон' }]}
          >
            <Select
              placeholder="Шаблон сезона"
              options={templates.map((t) => ({
                value: t.slug,
                label: `${t.title} (${t.slug}, ${t.durationDays}д)`,
              }))}
            />
          </Form.Item>
          <Form.Item
            name="mode"
            label="Режим"
            tooltip="Открытый: клиент играет сценарий офлайн и присылает лог действий целиком. Слепой weather-drip: логом владеет сервер, погода выдаётся по одному дню за коммит, поэтому знание будущей погоды как чит исключено. У слепого лог уже серверный — верификация только «на сабмите», вариант «ленивая» отключён (бэкенд отвечает 400)."
          >
            <Select
              options={[
                { value: 'open', label: 'Открытый (лог от клиента)' },
                { value: 'blind', label: 'Слепой weather-drip (лог на сервере)' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="window"
            label="Окно сабмитов"
            rules={[{ required: true, message: 'Задайте окно' }]}
          >
            <DatePicker.RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="attemptsLimit" label="Попыток на игрока">
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="verificationPolicy"
            label="Верификация"
            tooltip="inline: реплей в момент сабмита (дороже, сразу официально). lazy: сабмит дёшев, реплей — по кнопке (топ-N/аудит), невоспроизводимый клейм = дисквалификация. Для слепого режима lazy недоступна: лог копится на сервере и финализация реплеит его сразу."
          >
            <Select
              options={[
                { value: 'inline', label: 'На сабмите (inline)' },
                {
                  value: 'lazy',
                  label: 'Ленивая (RFC step 2)',
                  disabled: selectedMode === 'blind',
                },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </MainLayout>
  );
};

export default AdminSeasonsPage;
